import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * Coach Dashboard – Overview of all groups and their apprentices.
 * Route: /coach
 */
const CoachDashboard = () => {
    const { fetchWithAuth, user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [pendingReports, setPendingReports] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupApis, setGroupApis] = useState({}); // { groupId: [apprentices] }
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');
    const [announced, setAnnounced] = useState('');

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const [groupRes, pendingRes] = await Promise.all([
                fetchWithAuth('/api/coach/groups/'),
                fetchWithAuth('/api/coach/reports/pending/'),
            ]);
            const grps = await groupRes.json();
            const pending = await pendingRes.json();
            setGroups(Array.isArray(grps) ? grps : grps.results || []);
            setPendingReports(Array.isArray(pending) ? pending : []);
            if (grps.length > 0) setSelectedGroup(grps[0].id);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedGroup) return;
        fetchWithAuth(`/api/coach/groups/${selectedGroup}/apprentices/`)
            .then(r => r.json())
            .then(data => setGroupApis(prev => ({ ...prev, [selectedGroup]: Array.isArray(data) ? data : [] })))
            .catch(() => {});
    }, [selectedGroup]);

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        const res = await fetchWithAuth('/api/coach/groups/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: newGroupName }),
        });
        if (res.ok) {
            setNewGroupName('');
            setShowCreateModal(false);
            await loadDashboard();
        }
    };

    const apprentices = groupApis[selectedGroup] || [];

    const healthColor = (status) => ({
        green: 'bg-plantlix-400',
        yellow: 'bg-amber-400',
        red: 'bg-red-500',
    }[status] || 'bg-gray-400');

    const healthEmoji = (status) => ({ green: '🟢', yellow: '🟡', red: '🔴' }[status] || '⚪');

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Ausbilder-Dashboard</h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Alle deine Ausbildungsgruppen auf einen Blick</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => navigate('/coach/berichte')}
                        className="relative flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 font-semibold text-sm hover:bg-amber-100 dark:hover:bg-amber-900/30 transition"
                    >
                        📋 Berichte
                        {pendingReports.length > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                                {pendingReports.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => navigate('/coach/quizze')}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800 font-semibold text-sm hover:bg-purple-100 dark:hover:bg-purple-900/30 transition"
                    >
                        🧠 Quizze
                    </button>
                    <button
                        onClick={() => navigate('/coach/gruppen')}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-white/10 font-semibold text-sm hover:bg-gray-100 dark:hover:bg-white/10 transition"
                    >
                        ⚙️ Gruppen
                    </button>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition shadow-lg shadow-plantlix-500/20"
                    >
                        + Neue Gruppe
                    </button>
                </div>
            </div>

            {/* Pending reports banner */}
            {pendingReports.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-4 flex items-center gap-4">
                    <span className="text-2xl">📋</span>
                    <div className="flex-1">
                        <p className="font-bold text-amber-800 dark:text-amber-300 text-sm">
                            {pendingReports.length} Berichtswoche{pendingReports.length > 1 ? 'n' : ''} warten auf dein Abzeichen
                        </p>
                        <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
                            {pendingReports.slice(0, 3).map(r => r.apprentice_name).join(', ')}
                            {pendingReports.length > 3 && ` und ${pendingReports.length - 3} weitere…`}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/coach/berichte')}
                        className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition"
                    >
                        Jetzt prüfen
                    </button>
                </div>
            )}

            {/* Groups are empty */}
            {groups.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                    <p className="text-5xl mb-4">🌱</p>
                    <h2 className="font-bold text-lg text-gray-700 dark:text-gray-300 mb-2">Noch keine Gruppe erstellt</h2>
                    <p className="text-sm mb-6">Erstelle eine Gruppe und lade deine Azubis per QR-Code ein.</p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-6 py-3 rounded-2xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition"
                    >
                        Erste Gruppe erstellen
                    </button>
                </div>
            )}

            {/* Group tabs */}
            {groups.length > 0 && (
                <>
                    <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
                        {groups.map(g => (
                            <button
                                key={g.id}
                                onClick={() => setSelectedGroup(g.id)}
                                className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition border ${
                                    selectedGroup === g.id
                                        ? 'bg-plantlix-500 text-white border-plantlix-500 shadow-md'
                                        : 'bg-white dark:bg-white/5 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10'
                                }`}
                            >
                                {g.name}
                                <span className="ml-2 opacity-60 text-xs">{g.member_count}</span>
                            </button>
                        ))}
                    </div>

                    {/* Apprentice grid */}
                    {apprentices.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <p className="text-4xl mb-3">👥</p>
                            <p className="text-sm">Noch keine Azubis in dieser Gruppe mit DSGVO-Zustimmung.</p>
                            <button
                                onClick={() => navigate('/coach/gruppen')}
                                className="mt-4 px-4 py-2 rounded-xl bg-plantlix-500 text-white text-sm font-semibold hover:bg-plantlix-600 transition"
                            >
                                QR-Code anzeigen
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {apprentices.map(azubi => (
                                <button
                                    key={azubi.id}
                                    onClick={() => navigate(`/coach/azubi/${azubi.id}`)}
                                    className="text-left group bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-2xl p-5 hover:border-plantlix-200 dark:hover:border-plantlix-800 hover:shadow-lg transition-all"
                                >
                                    <div className="flex items-start gap-4">
                                        {/* Avatar / Initials */}
                                        <div className="relative flex-shrink-0">
                                            {azubi.avatar ? (
                                                <img src={azubi.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-plantlix-100 dark:bg-plantlix-900/30 flex items-center justify-center text-plantlix-600 dark:text-plantlix-400 font-bold text-lg">
                                                    {(azubi.first_name?.[0] || azubi.username?.[0] || '?').toUpperCase()}
                                                </div>
                                            )}
                                            <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-[#1a1a1a] ${healthColor(azubi.health_status)}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-gray-900 dark:text-white truncate">
                                                {azubi.first_name ? `${azubi.first_name} ${azubi.last_name}` : azubi.username}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{azubi.last_login_display}</p>
                                        </div>
                                        {azubi.pending_reports > 0 && (
                                            <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold px-2 py-1 rounded-lg">
                                                {azubi.pending_reports} offen
                                            </span>
                                        )}
                                    </div>

                                    {/* Progress bar */}
                                    <div className="mt-4 space-y-1.5">
                                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                            <span>Lernfortschritt</span>
                                            <span>{azubi.learned_count} gelernt · {azubi.streak} Tage Streak</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-gray-100 dark:bg-white/10 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-plantlix-400 transition-all"
                                                style={{ width: `${Math.min(100, (azubi.learned_count / Math.max(1, azubi.learned_count + azubi.in_progress_count + azubi.not_learned_count)) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Create group modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-6">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
                    <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 dark:border-white/5">
                        <h2 className="font-bold text-lg text-gray-900 dark:text-white mb-4">Neue Gruppe erstellen</h2>
                        <input
                            value={newGroupName}
                            onChange={e => setNewGroupName(e.target.value)}
                            placeholder="z.B. 1. Lehrjahr 2025 – Musterbetrieb"
                            onKeyDown={e => e.key === 'Enter' && handleCreateGroup()}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-400 mb-4"
                        />
                        <div className="flex gap-3">
                            <button onClick={handleCreateGroup} className="flex-1 py-2.5 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition">
                                Erstellen
                            </button>
                            <button onClick={() => setShowCreateModal(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 text-sm transition hover:bg-gray-50 dark:hover:bg-white/5">
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoachDashboard;
